import { axios_instance } from "@/api/api";
import { router } from "@/router/router";
import { useLocalStorage } from "@vueuse/core";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { toast } from "vue3-toastify";

export const use_auth = defineStore("auth", () => {
  const username = ref("");
  const password = ref("");
  const loading = ref<boolean>(false);

  const user = useLocalStorage("user", "");
  const role = useLocalStorage("user_role", "operator");
  const avatarSeed = useLocalStorage("user_avatar_seed", "");

  // Derive isLoggedIn from stored user, not a separate flag
  const isLoggedIn = computed(() => {
    return user.value !== "";
  });

  const isAdmin = computed(() => role.value === "admin");

  // Validate session at most once per TTL — never block a route change on a
  // network round-trip (that was the tab-switch lag). First navigation after
  // boot still hits /me (fix from 46e09c8 preserved); the shared API interceptor
  // refreshes once on protected-request 401s and redirects only if refresh fails.
  const ME_TTL_MS = 60_000;
  let me_checked_at = 0;

  const fetchMeThrottled = async () => {
    if (Date.now() - me_checked_at < ME_TTL_MS) return;
    me_checked_at = Date.now();
    await fetchMe();
  };

  const fetchMe = async () => {
    try {
      const res = await axios_instance.get("/api/v1/view/me");
      const info = res?.data?.user as
        { username?: string; role?: string; avatar_seed?: string } | undefined;
      if (info?.username) {
        user.value = info.username;
        if (info.role) role.value = info.role;
        avatarSeed.value = info.avatar_seed || info.username;
      }
    } catch {
      // /me failed or token expired — user will be redirected by 401 interceptor
    }
  };

  const login = async () => {
    if (!username.value || !password.value) {
      return;
    }
    const data = JSON.stringify({
      username: username.value,
      password: password.value,
    });
    const config = {
      method: "post",
      maxBodyLength: Infinity,
      url: "/api/v1/view/login",
      headers: {
        "Content-Type": "application/json",
      },
      data: data,
    };
    loading.value = true;
    axios_instance
      .request<Record<string, unknown>>(config)
      .then(async (res) => {
        user.value = username.value;
        const userInfo = res?.data?.user as
          { username?: string; role?: string } | undefined;
        if (userInfo?.role) {
          role.value = userInfo.role;
        } else {
          role.value = "operator";
        }
        // Sync role from DB via /me
        await fetchMe();
        router.push("/");
      })
      .catch((error) => {
        toast.error(error);
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const clearAuth = () => {
    user.value = "";
    role.value = "operator";
    avatarSeed.value = "";
    router.push("/login");
  };

  const logout = async () => {
    try {
      await axios_instance.post("/api/v1/view/logout");
    } catch {
      // Server may be down; clear locally anyway.
    } finally {
      clearAuth();
    }
  };

  return {
    username,
    password,
    login,
    logout,
    clearAuth,
    loading,
    isLoggedIn,
    user,
    role,
    isAdmin,
    avatarSeed,
    fetchMe,
    fetchMeThrottled,
  };
});
